import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, queryMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';
import { processJob } from './jobSlice';
import { deepEquals} from '../common/utils/objectUtilities';

const requestableJobsSlice = createSlice({
    name: 'requestableJobs',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
        state.data = {}
        for (const job of ensureArray(action.payload).map(processJob)) {
            state.data[job.jobid] = job;
        }
        },
        addOrUpdate: (state, action) => {
            for (const job of ensureArray(action.payload).map(processJob)) {
                const id = job.jobid?.toString();
                    if (!state.data[id] || !deepEquals(state.data[id], job)) {
                        state.data[id] = job;
                    }
            }
        },
        remove: (state, action) => {
            for (const job of ensureArray(action.payload)) {
                const id = job.jobid?.toString();
                if (state.data[id]) {
                    delete state.data[id];
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => requestableJobsSlice.getInitialState());
    }
});

const fetchRequestableJobs = network => network.get('/api/v1/requestableJobs', requestableJobsSlice.actions.set);
const fetchRequestableJob = jobid => network => network.get('/api/v1/requestableJob/'+ encodeURIComponent(jobid), requestableJobsSlice.actions.addOrUpdate);

// #region selectors
const selectAllRequestableJobs = state => state.requestableJobs.data;
const selectRequestableJobs = state => Object.values(selectAllRequestableJobs(state));
const selectRequestableJob = jobid => createSelector([selectAllRequestableJobs], jobs => jobs[jobid]);

// #region listeners
const listen = {
    requestableJobAdded: (job, { dispatch }) => dispatch(requestableJobsSlice.actions.addOrUpdate(job)),
    requestableJobUpdated: (job, { dispatch }) => dispatch(requestableJobsSlice.actions.addOrUpdate(job)),
    requestableJobRemoved: (job, { dispatch }) => dispatch(requestableJobsSlice.actions.remove(job))
};
// #endregion
const initByID = initMapFactory(fetchRequestableJob, listen, { name: 'jobById' })

const cleanup = { cleanup: { root: 'requestableJobs', all: selectRequestableJobs, remove: requestableJobsSlice.actions.remove, removeAll: requestableJobsSlice.actions.remove }};

export const [ useRequestableJobs ] = dataHookFactory(selectRequestableJobs, queryMapFactory(fetchRequestableJobs,'jobid', initByID, listen, { allowEmpty: true })(), cleanup);
export const [ useRequestableJob ] = dataHookFactory(selectRequestableJob, initByID, cleanup);

const reducerDetails = { name: requestableJobsSlice.name, reducer: requestableJobsSlice.reducer };
export default reducerDetails;